import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { SiteHeader } from '../../components/SiteLayout';
import {
  CheckCircle, Warning, X, Clock, FileText, ArrowRight, Sparkle,
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({ baseURL: `${API}/api/hale-marketplace/admin`, withCredentials: true });

/* /assessor/hale-applications — admin review queue.
 *
 * Single-pane workflow: list on the left, full application detail on the
 * right with reviewer-decision panel (4 statuses: approved / approved_with_recommendations
 * / pending_revision / not_approved).
 *
 * Renders inside the standard A^G assessor chrome so it sits naturally next
 * to Hale Reviewed, Hale Command, and Formula DB.
 */

const STATUS_META = {
  pending_review:                   { color: 'text-[#E63946] bg-[#FEE2E2]', label: 'Pending review',  Icon: Clock },
  approved:                         { color: 'text-[#03543F] bg-[#D1FAE5]', label: 'Approved',         Icon: CheckCircle },
  approved_with_recommendations:    { color: 'text-[#723B13] bg-[#FEF3C7]', label: 'Approved · w/ recs',  Icon: CheckCircle },
  pending_revision:                 { color: 'text-[#723B13] bg-[#FEF3C7]', label: 'Pending revision', Icon: Warning },
  not_approved:                     { color: 'text-[#9B1C1C] bg-[#FEE2E2]', label: 'Not approved',     Icon: X },
  draft:                            { color: 'text-gray-500 bg-gray-100',  label: 'Draft',            Icon: Clock },
};

function StatusPill({ status }) {
  const meta = STATUS_META[status] || STATUS_META.draft;
  const I = meta.Icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded ${meta.color}`}>
      <I size={11} weight="fill" /> {meta.label}
    </span>
  );
}

export default function HaleApplicationsAdminPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [statusFilter, setStatusFilter] = useState('pending_review');
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await ax.get('/applications', { params: { status: statusFilter || undefined, limit: 100 } });
      setItems(r.data.items || []); setTotal(r.data.total || 0);
      if (!selectedId && r.data.items?.[0]) setSelectedId(r.data.items[0].id);
    } catch (e) { toast.error('Failed to load applications'); }
    setLoading(false);
  };

  const loadDetail = async (id) => {
    if (!id) { setDetail(null); return; }
    try { const r = await ax.get(`/applications/${id}`); setDetail(r.data); }
    catch (e) { toast.error('Could not load application'); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);
  useEffect(() => { loadDetail(selectedId); }, [selectedId]);

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      <SiteHeader />
      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Sparkle size={20} weight="fill" className="text-[#E63946]" />
              <h1 className="text-2xl font-bold tracking-tight text-[#111827]">HALE Brand Applications</h1>
              <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#4B5563] bg-[#F3F4F6] px-2 py-0.5 rounded ml-2">admin only</span>
            </div>
            <p className="text-sm text-[#4B5563]">
              Review and decision incoming HALE Reviewed™ / Verified™ applications.
            </p>
          </div>
          <Link to="/assessor">
            <Button variant="outline" className="h-9 text-xs rounded">← Assessor Console</Button>
          </Link>
        </div>

        {/* Filter strip */}
        <div className="flex gap-2 mb-5 flex-wrap" data-testid="status-filter">
          {[
            ['', 'All'],
            ['pending_review', 'Pending review'],
            ['approved', 'Approved'],
            ['approved_with_recommendations', 'Approved · w/ recs'],
            ['pending_revision', 'Pending revision'],
            ['not_approved', 'Not approved'],
          ].map(([k, l]) => (
            <button key={k} onClick={() => setStatusFilter(k)}
              className={`px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider rounded border ${
                statusFilter === k ? 'bg-[#0B1426] text-[#0B1426] border-[#0B1426]'
                                   : 'bg-white text-[#4B5563] border-gray-200 hover:border-gray-300'
              }`}
              data-testid={`filter-${k || 'all'}`}>{l}</button>
          ))}
        </div>

        <div className="grid lg:grid-cols-12 gap-4">
          {/* Queue */}
          <div className="lg:col-span-4 card-flat overflow-hidden">
            <div className="px-3 py-2 text-[10px] font-bold uppercase tracking-[0.2em] text-[#4B5563] bg-[#F3F4F6] border-b border-gray-200">
              {loading ? 'Loading…' : `${total} application${total === 1 ? '' : 's'}`}
            </div>
            <div className="divide-y divide-gray-100 max-h-[70vh] overflow-y-auto" data-testid="applications-list">
              {items.length === 0 && !loading && (
                <div className="px-4 py-8 text-center text-xs text-[#4B5563]">No applications match this filter.</div>
              )}
              {items.map(a => (
                <button key={a.id} onClick={() => setSelectedId(a.id)}
                  className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition ${
                    selectedId === a.id ? 'bg-[#FEE2E2]/30' : ''
                  }`} data-testid={`row-${a.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-bold text-[#111827] truncate">{a.draft?.brand?.brand_name || '(unnamed brand)'}</p>
                    <StatusPill status={a.status} />
                  </div>
                  <p className="text-[10px] text-[#4B5563] mt-0.5 truncate">{a.draft?.founder?.full_name} · {a.email_masked}</p>
                  <p className="text-[10px] uppercase tracking-wider text-[#9B1C1C] mt-1">
                    {a.recommendation?.track || '—'} · {a.recommendation?.tier || '—'}
                  </p>
                </button>
              ))}
            </div>
          </div>

          {/* Detail */}
          <div className="lg:col-span-8">
            {detail ? <ApplicationDetail app={detail} onUpdated={() => { load(); loadDetail(detail.id); }} />
                    : <div className="card-flat p-8 text-sm text-[#4B5563]">Select an application from the queue.</div>}
          </div>
        </div>
      </main>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mb-5">
      <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#4B5563] mb-2">{title}</p>
      {children}
    </div>
  );
}
function KV({ k, v }) {
  return (
    <div className="flex gap-3 py-1 text-xs">
      <span className="w-40 flex-shrink-0 text-[#6B7280]">{k}</span>
      <span className="text-[#111827] flex-1 break-words">{v || '—'}</span>
    </div>
  );
}

function ApplicationDetail({ app, onUpdated }) {
  const [decision, setDecision] = useState('approved');
  const [notes, setNotes] = useState('');
  const [recs, setRecs] = useState('');
  const [busy, setBusy] = useState(false);
  const draft = app.draft || {};
  const rec = app.recommendation || {};

  const decide = async () => {
    if (!notes.trim() || notes.trim().length < 10) {
      toast.error('Reviewer notes (min 10 chars) required'); return;
    }
    setBusy(true);
    try {
      await ax.patch(`/applications/${app.id}/decision`, {
        status: decision, reviewer_notes: notes,
        recommendations: recs ? recs.split('\n').map(s => s.trim()).filter(Boolean) : [],
      });
      toast.success('Decision recorded');
      onUpdated?.();
      setNotes(''); setRecs('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Decision failed');
    }
    setBusy(false);
  };

  return (
    <div className="card-flat p-6" data-testid={`detail-${app.id}`}>
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h2 className="text-xl font-bold text-[#111827]">{draft.brand?.brand_name || '(unnamed brand)'}</h2>
          <p className="text-sm text-[#4B5563]">{draft.founder?.full_name} · {app.email}</p>
        </div>
        <div className="text-right">
          <StatusPill status={app.status} />
          {app.submitted_at && <p className="text-[10px] text-[#6B7280] mt-1">Submitted {new Date(app.submitted_at).toLocaleDateString()}</p>}
        </div>
      </div>

      {/* Recommendation block */}
      {rec.track && (
        <div className="rounded-lg border border-[#E63946]/30 bg-[#E63946]/5 p-4 mb-5">
          <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#E63946] mb-2">Recommended pathway</p>
          <p className="text-base font-bold text-[#111827]">
            HALE {rec.track === 'both' ? 'Reviewed™ + Verified™' : rec.track === 'hale_reviewed' ? 'Reviewed™' : 'Verified™'}
            <span className="ml-2 text-[#9B1C1C]">· {rec.tier} tier</span>
          </p>
          {rec.rationale && <p className="text-[13px] text-[#4B5563] mt-2 leading-relaxed">{rec.rationale}</p>}
          {rec.primary_drivers?.length > 0 && (
            <ul className="mt-3 space-y-1">
              {rec.primary_drivers.map((d, i) => (
                <li key={i} className="text-[12px] text-[#4B5563] flex items-start gap-2">
                  <ArrowRight size={11} className="mt-1" /> {d}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Application data */}
      <div className="grid sm:grid-cols-2 gap-5">
        <Section title="Founder">
          <KV k="Name" v={draft.founder?.full_name} />
          <KV k="Role" v={draft.founder?.role} />
        </Section>
        <Section title="Brand">
          <KV k="Brand" v={draft.brand?.brand_name} />
          <KV k="Website" v={draft.brand?.website} />
          <KV k="Country" v={draft.brand?.country} />
          <KV k="Founded" v={draft.brand?.founded_year} />
        </Section>
        <Section title="Portfolio">
          <KV k="Categories" v={(draft.products?.categories || []).join(', ')} />
          <KV k="SKU count" v={draft.products?.sku_count} />
          <KV k="Retailer goals" v={(draft.products?.retailer_goals || []).join(', ')} />
          <KV k="Distribution" v={(draft.products?.distribution || []).join(', ')} />
        </Section>
        <Section title="Claims posture">
          <KV k="Marketing claims" v={draft.claims?.has_marketing_claims ? 'Yes' : 'Minimal'} />
          <KV k="Fragrance" v={draft.claims?.fragrance_disclosure} />
          <KV k="Ingredients" v={draft.claims?.ingredient_transparency} />
        </Section>
        <Section title="Packaging">
          <KV k="Materials" v={(draft.packaging?.materials || []).join(', ')} />
          <KV k="PCR" v={draft.packaging?.pcr_content} />
          <KV k="Refillable" v={draft.packaging?.refillable ? 'Yes' : '—'} />
          <KV k="FSC certified" v={draft.packaging?.fsc_certified ? 'Yes' : '—'} />
        </Section>
        <Section title="Sustainability">
          <KV k="Declarations" v={(draft.sustainability?.declarations || []).join(', ')} />
        </Section>
        {draft.brand?.brand_story && (
          <div className="sm:col-span-2">
            <Section title="Brand story">
              <p className="text-[13px] text-[#374151] leading-relaxed">{draft.brand.brand_story}</p>
            </Section>
          </div>
        )}
        {draft.sustainability?.impact_narrative && (
          <div className="sm:col-span-2">
            <Section title="Impact narrative">
              <p className="text-[13px] text-[#374151] leading-relaxed">{draft.sustainability.impact_narrative}</p>
            </Section>
          </div>
        )}
        {draft.claims?.claims_examples && (
          <div className="sm:col-span-2">
            <Section title="Claims examples">
              <p className="text-[13px] text-[#374151] leading-relaxed font-mono">{draft.claims.claims_examples}</p>
            </Section>
          </div>
        )}
      </div>

      {/* Decision panel */}
      {app.status === 'pending_review' && (
        <div className="mt-6 border-t border-gray-200 pt-5" data-testid="decision-panel">
          <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#4B5563] mb-3">Reviewer decision</p>
          <div className="flex flex-wrap gap-2 mb-3">
            {[
              ['approved', 'Approved', 'bg-[#03543F] text-[#0B1426]'],
              ['approved_with_recommendations', 'Approve · w/ recommendations', 'bg-[#723B13] text-[#0B1426]'],
              ['pending_revision', 'Pending revision', 'bg-[#723B13] text-[#0B1426]'],
              ['not_approved', 'Not approved', 'bg-[#9B1C1C] text-[#0B1426]'],
            ].map(([k, l, cls]) => (
              <button key={k} onClick={() => setDecision(k)}
                className={`px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider rounded ${
                  decision === k ? cls : 'bg-white text-[#4B5563] border border-gray-200'
                }`}
                data-testid={`decision-${k}`}>{l}</button>
            ))}
          </div>
          <Textarea rows={3} value={notes} onChange={e => setNotes(e.target.value)}
            placeholder="Reviewer notes (founder-facing)" className="mb-2" data-testid="decision-notes" />
          <Textarea rows={3} value={recs} onChange={e => setRecs(e.target.value)}
            placeholder="Recommendations · one per line (optional)" className="mb-3" data-testid="decision-recs" />
          <Button onClick={decide} disabled={busy}
            className="bg-[#E63946] hover:bg-[#C9192C] text-black h-9 text-xs font-bold uppercase tracking-wider rounded"
            data-testid="decision-submit">
            {busy ? 'Recording…' : 'Record decision'}
          </Button>
        </div>
      )}

      {/* Audit trail */}
      {app.audit?.length > 0 && (
        <div className="mt-6 border-t border-gray-200 pt-5">
          <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#4B5563] mb-3">Audit trail</p>
          <ul className="space-y-2">
            {app.audit.slice(-10).reverse().map((a, i) => (
              <li key={i} className="text-xs text-[#374151]">
                <span className="font-mono text-[#6B7280]">{new Date(a.decided_at).toLocaleString()}</span>{' '}
                · <span className="font-bold">{a.status}</span>
                {a.decided_by && <span className="text-[#6B7280]"> by {a.decided_by}</span>}
                {a.reviewer_notes && <span className="block ml-3 text-[#4B5563]">{a.reviewer_notes}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
